import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../Style/SharingMap.css'; 

// Asset Imports
import carGreenSvg from '../assets/car-green.svg';
import carYellowSvg from '../assets/car-yellow.svg';
import carRedSvg from '../assets/car-red.svg';
import carBlackSvg from '../assets/car-black.svg';
import carBlueSvg from '../assets/car-blue.svg';
import matrix from '../assets/matrix.png'; // Ensure this path is correct
import { PubSub, Amplify } from 'aws-amplify';

const API_BASE_URL = "https://api.yellowmatrix.co.in";

// --- Constants (From VehicleDetails) ---
const DEFAULT_LIVE_MAP_ZOOM = 17;                        
const FLY_ANIMATION_DURATION = 1.5;

// --- Helpers ---
const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3;
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const calculateBearing = (from, to) => {
    if (!from || !to) return 0;
    const φ1 = from.Latitude * Math.PI / 180;
    const φ2 = to.Latitude * Math.PI / 180;
    const Δλ = (to.Longitude - from.Longitude) * Math.PI / 180;
    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    let θ = Math.atan2(y, x);
    return (θ * 180 / Math.PI + 360) % 360;
};

// Exact rotation helper from VehicleDetails
const getShortestRotation = (currentBearing, newBearing) => {
    currentBearing = ((currentBearing % 360) + 360) % 360;
    newBearing = ((newBearing % 360) + 360) % 360;
    let diff = newBearing - currentBearing;
    if (diff > 180) diff -= 360;
    else if (diff < -180) diff += 360;
    return currentBearing + diff;
};

const parsePVT = (pvtString) => {
    if (!pvtString || !pvtString.startsWith('$,PVT')) return null;
    const parts = pvtString.split(',');
    const convertDMM = (val, dir) => {
        const deg = Math.floor(val / 100);
        const dec = deg + (val - deg * 100) / 60;
        return dir === 'S' || dir === 'W' ? -dec : dec;
    };
    return {
        VehicleID: parts[7],
        GPSFix: parseInt(parts[8]),
        Latitude: convertDMM(parseFloat(parts[11]), parts[12]),
        Longitude: convertDMM(parseFloat(parts[13]), parts[14]),
        Speed: parseFloat(parts[15]),
        Ignition: parseInt(parts[22]),
        Bearing: parts.length > 23 ? parseFloat(parts[23]) : 0,
        LastTime: Date.now()
    };
};

const getVehicleStatus = (v) => {
    if (!v) return 'CONNECTING...';
    if (v.GPSFix === 0 || !v.Latitude) return 'NO GPS';
    if (v.Speed > 3 && v.Ignition === 1) return 'RUNNING';
    if (v.Ignition === 1 && v.Speed <= 3) return 'IDLE';
    if (v.Ignition === 0) return 'HALT';
    return 'UNKNOWN';
};

const vehicleIcons = { 
    'RUNNING': carGreenSvg, 
    'IDLE': carYellowSvg, 
    'HALT': carRedSvg, 
    'NO GPS': carBlackSvg, 
    'UNKNOWN': carBlueSvg 
};

const SharingMap = () => {
    const { token } = useParams();
    const [address, setAddress] = useState(null);
    const [liveVehicleData, setLiveVehicleData] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState('Initializing...');

    // Refs
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const smoothRotationRef = useRef(0); 
    const previousDataRef = useRef(null);
    const lastAddressFetchRef = useRef(0);
    const mappedVehicleIdRef = useRef(null); 

    // --- INTEGRATION---
    const { data: publicConfig, isError, error } = useQuery({
        queryKey: ['publicLive', token],
        queryFn: async () => {
            const res = await fetch(`${API_BASE_URL}/public/live/${token}`);
            if (res.status === 410) throw new Error("Link Expired");
            if (!res.ok) throw new Error("Invalid Link");
            return res.json();
        },
        retry: false
    });

    // --- INTEGRATION: MQTT/Amplify (Untouched) ---
    useEffect(() => {
        if (publicConfig?.mqtt) {
            setConnectionStatus('Connecting...');
            const { identityPoolId, region, endpoint, topic } = publicConfig.mqtt;
            try {
                Amplify.configure({
                    Auth: { identityPoolId, region, mandatorySignIn: false },
                    API: { endpoints: [{ name: "AWSIoTDataProvider", endpoint, region }] }
                });
                const sub = PubSub.subscribe(topic).subscribe({
                    next: (data) => {
                        const parsed = parsePVT(data.value?.PVT);
                        if (parsed) {
                            setConnectionStatus('Live');
                            setLiveVehicleData(current => {
                                const prev = previousDataRef.current || current;
                                let bearing = parsed.Bearing;
                                if (prev?.Latitude && parsed.Latitude) {
                                    const dist = haversineDistance(prev.Latitude, prev.Longitude, parsed.Latitude, parsed.Longitude);
                                    // Use calculated bearing if moving > 10m
                                    bearing = dist > 10 ? calculateBearing(prev, parsed) : (prev.calculatedBearing || parsed.Bearing);
                                }
                                parsed.calculatedBearing = bearing;
                                previousDataRef.current = parsed;
                                return parsed;
                            });
                        }
                    },
                    error: (err) => setConnectionStatus('Error')
                });
                return () => sub.unsubscribe();
            } catch (e) { setConnectionStatus('Config Error'); }
        }
    }, [publicConfig]);

    // --- INTEGRATION: Address Lookup (Untouched) ---
    useEffect(() => {
        if (liveVehicleData?.Latitude && liveVehicleData?.Longitude) {
            const now = Date.now();
            if (now - lastAddressFetchRef.current < 10000) return;
            fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${liveVehicleData.Latitude}&lon=${liveVehicleData.Longitude}`, {
                headers: { 'User-Agent': 'TrackMasterHub/1.0' }
            })
                .then(r => r.json())
                .then(d => {
                    setAddress(d.display_name);
                    lastAddressFetchRef.current = now;
                })
                .catch(() => setAddress("Address unavailable"));
        }
    }, [liveVehicleData?.Latitude, liveVehicleData?.Longitude]);


    // --- MAP LOGIC ---
    useEffect(() => {
        // 1. Initialize Map if needed
        if (!mapRef.current) {
            const mapContainer = document.getElementById('track-map-instance');
            if (mapContainer) {
                mapRef.current = L.map('track-map-instance').setView([20.5937, 78.9629], 5);
                L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(mapRef.current);
            }
        }

        // 2. Map Rendering Logic (Exact match from VehicleDetails)
        if (liveVehicleData && liveVehicleData.Latitude && mapRef.current) {
            const { Latitude, Longitude, calculatedBearing } = liveVehicleData;
            const status = getVehicleStatus(liveVehicleData);
            const iconUrl = vehicleIcons[status] || vehicleIcons.UNKNOWN;
            
            // Map Fly/View Logic
            // If this is the first time mapping this vehicle (or page load), fly to it
            if (liveVehicleData.VehicleID !== mappedVehicleIdRef.current) {
                mapRef.current.flyTo([Latitude, Longitude], DEFAULT_LIVE_MAP_ZOOM, { duration: FLY_ANIMATION_DURATION });
                mappedVehicleIdRef.current = liveVehicleData.VehicleID;
            } else {
                // Otherwise just set view to keep it centered
                mapRef.current.setView([Latitude, Longitude], DEFAULT_LIVE_MAP_ZOOM);
            }

            // Marker Logic
            if (markerRef.current) {
                markerRef.current.setLatLng([Latitude, Longitude]);
                const iconElement = markerRef.current.getElement();
                
                if (iconElement) {
                    const img = iconElement.querySelector('.vehicle-icon-image');
                    if (img) {
                        if (img.src !== iconUrl) img.src = iconUrl;
                        
                        const currentRotation = smoothRotationRef.current;
                        const newRotation = getShortestRotation(currentRotation, calculatedBearing);
                        
                        // Exact transition style from VehicleDetails for smooth rotation
                        img.style.transition = 'transform 0.7s ease-in-out';
                        img.style.transform = `rotate(${newRotation}deg)`;
                        
                        smoothRotationRef.current = newRotation;
                    }
                }
            } else {
                // Initial Marker Creation using DivIcon
                const icon = L.divIcon({
                    className: "custom-leaflet-div-icon",
                    html: `<img src="${iconUrl}" class="vehicle-icon-image" style="transform: rotate(${calculatedBearing}deg);">`,
                    iconSize: [37, 37],
                    iconAnchor: [18, 18],
                });
                markerRef.current = L.marker([Latitude, Longitude], { icon }).addTo(mapRef.current);
                smoothRotationRef.current = calculatedBearing;
            }
        }
        
        return () => {
            // Cleanup logic if needed
        };
    }, [liveVehicleData]);

    if (isError) return <div className="TrackMap-container"><h2>Error</h2><p>{error.message}</p></div>;

    return (
        <>
            {/* --- TOP NAVBAR (Exact match from MapTracker reference) --- */}
            <nav className="custom-top-navbar">
                <div className="navbar-left">
                    <div className="brand-link"><h3 className="brand-text">TrackMaster Hub</h3></div>
                    <div className="nav-divider"></div>
                    <div className="company-badge">
                        <img src={matrix} alt="Yellow Matrix Logo" className="company-logo-img" />
                        <span className="company-text">Yellow Matrix Private Limited</span>
                    </div>
                </div>
                <div className="navbar-right">
                    <div className="brand-link"><p className="nav-link">MOBILE APPS</p></div>
                    <div className="brand-link"><p className="nav-link">LIVE ALERTS</p></div>
                    <div className="brand-link"><p className="nav-link">SLIDE SHOW</p></div>
                    <div className="brand-link"><p className="nav-link">?</p></div>
                    <div className="navbar-live-badge">
                        <span className="pulse-dot"></span>
                        <span className="nav-track">{connectionStatus.toUpperCase()}</span>
                    </div>
                </div>
            </nav>

            <div className="TrackMap-container">
                <div className="TrackMap-layout">
                    <div className="TrackMap-sidebar">
                        <div className="TrackMap-info">
                            {!liveVehicleData ? <div className="skeleton">Waiting for signal...</div> : (
                                <>
                                    <h3 className="TrackMap-label-bold">VEHICLE : {liveVehicleData.VehicleID}</h3>
                                <div className="TrackMap-addr-section">
                                   <div className="TrackMap-label-container">
                                    <span className="TrackMap-pulse-marker"></span>
                                    <small className="TrackMap-muted">Location</small>
                                    </div>
                                    <p className="TrackMap-address-bold">{address || 'Fetching...'}</p>
                                </div>
                                    <div className="TrackMap-grid">
                                        <div className="TrackMap-cell">
                                            <small className="TrackMap-muted">Status</small>
                                            <div className="TrackMap-status-text">{getVehicleStatus(liveVehicleData)}</div>
                                        </div>
                                        <div className="TrackMap-cell">
                                        <small className="TrackMap-muted">Last Update</small>
                                        <p className="TrackMap-address-bold">{new Date(liveVehicleData.LastTime).toLocaleTimeString()}</p>
                                    </div>
                                        <div className="TrackMap-cell">
                                            <small className="TrackMap-muted">Speed</small>
                                            <strong>{liveVehicleData.Speed?.toFixed(1)} km/h</strong>
                                        </div>
                                    </div>
                                    
                                </>
                            )}
                        </div>
                    </div>
                    <div className="TrackMap-map-area">
                        <div className="TrackMap-live-indicator"><span className="TrackMap-dot"></span>Live Tracking</div>
                        <div id="track-map-instance" className="TrackMap-leaflet-container"></div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SharingMap;